// Small building blocks for the sign-in, verify, reset and account screens, styled like the iOS sign-in sheet:
// tonal rounded fields, an orange primary button, secondary outline buttons, red error text. Tailwind utilities with
// the design tokens from src/ui/tokens.css (the shell's shared components are built in parallel, so auth keeps its
// own).

import { clsx } from 'clsx';
import { Eye, EyeOff, LoaderCircle, TriangleAlert } from 'lucide-react';
import { useId, type ButtonHTMLAttributes, type ComponentPropsWithRef, type InputHTMLAttributes, type ReactNode } from 'react';

export function Spinner({ className }: { className?: string }) {
  return <LoaderCircle aria-hidden className={clsx('animate-spin', className ?? 'h-4 w-4')} />;
}

/** Small uppercase-ish label above a field (iOS SectionLabel). */
export function FieldLabel({ htmlFor, children, trailing }: { htmlFor?: string; children: ReactNode; trailing?: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <label htmlFor={htmlFor} className="text-xs font-semibold tracking-wide text-slate-500 uppercase dark:text-slate-400">
        {children}
      </label>
      {trailing}
    </div>
  );
}

const fieldClass =
  'w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-base text-slate-900 outline-none transition '
  + 'placeholder:text-slate-400 focus:border-[var(--gt-orange)] focus:bg-white focus:ring-2 focus:ring-[var(--gt-orange)]/25 '
  + 'disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:bg-slate-900';

/** Text field (React 19: `ref` is an ordinary prop and reaches the input). */
export function TextInput({ className, ...props }: ComponentPropsWithRef<'input'>) {
  return <input {...props} className={clsx(fieldClass, className)} />;
}

/** Secure field with a show / hide button (iOS PasswordEntryField); `revealed` is shared so a confirm field follows. */
export function PasswordInput({
  revealed,
  onToggleReveal,
  showsRevealButton = true,
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { revealed: boolean; onToggleReveal?: () => void; showsRevealButton?: boolean }) {
  return (
    <div className="relative">
      <input
        {...props}
        type={revealed ? 'text' : 'password'}
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        className={clsx(fieldClass, showsRevealButton && 'pr-12', revealed && 'font-mono', className)}
      />
      {showsRevealButton && onToggleReveal && (
        <button
          type="button"
          onClick={onToggleReveal}
          className="absolute inset-y-0 right-2 my-auto flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-200/60 dark:hover:bg-slate-700"
          aria-label={revealed ? 'Hide password' : 'Show password'}
        >
          {revealed ? <EyeOff className="h-4 w-4" aria-hidden /> : <Eye className="h-4 w-4" aria-hidden />}
        </button>
      )}
    </div>
  );
}

/** A labelled field block. */
export function Field({
  label,
  trailing,
  children,
}: {
  label: string;
  trailing?: ReactNode;
  children: (id: string) => ReactNode;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-2">
      <FieldLabel htmlFor={id} trailing={trailing}>
        {label}
      </FieldLabel>
      {children(id)}
    </div>
  );
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  busy?: boolean;
  busyLabel?: string;
  icon?: ReactNode;
};

function ButtonContent({ busy, busyLabel, icon, children }: Pick<ButtonProps, 'busy' | 'busyLabel' | 'icon' | 'children'>) {
  return (
    <>
      {busy ? <Spinner /> : icon}
      <span>{busy && busyLabel ? busyLabel : children}</span>
    </>
  );
}

/** Orange, full width (iOS primaryGlassButton). */
export function PrimaryButton({ busy, busyLabel, icon, children, className, disabled, type = 'button', ...props }: ButtonProps) {
  return (
    <button
      {...props}
      type={type}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      className={clsx(
        'flex w-full items-center justify-center gap-2 rounded-2xl bg-[var(--gt-orange)] px-4 py-3 text-sm font-bold text-white shadow-sm transition',
        'hover:brightness-105 focus-visible:ring-2 focus-visible:ring-[var(--gt-orange)]/40 focus-visible:outline-none active:brightness-95',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
    >
      <ButtonContent busy={busy} busyLabel={busyLabel} icon={icon}>
        {children}
      </ButtonContent>
    </button>
  );
}

/** Outline, full width (iOS secondaryGlassButton). `tone="danger"` for destructive actions. */
export function SecondaryButton({
  busy,
  busyLabel,
  icon,
  children,
  className,
  disabled,
  tone = 'default',
  type = 'button',
  ...props
}: ButtonProps & { tone?: 'default' | 'danger' | 'navy' }) {
  return (
    <button
      {...props}
      type={type}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      className={clsx(
        'flex w-full items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-sm font-bold transition',
        'focus-visible:ring-2 focus-visible:ring-slate-400/40 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
        tone === 'danger' && 'border-[var(--gt-red)]/30 bg-white text-[var(--gt-red)] hover:bg-[var(--gt-red)]/5 dark:bg-slate-900',
        tone === 'navy' && 'border-transparent bg-[var(--gt-navy)] text-white hover:brightness-110',
        tone === 'default' && 'border-slate-200 bg-white text-slate-800 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800',
        className,
      )}
    >
      <ButtonContent busy={busy} busyLabel={busyLabel} icon={icon}>
        {children}
      </ButtonContent>
    </button>
  );
}

/** A plain text button (links like "Forgot password?"). */
export function TextButton({ className, tone = 'orange', ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { tone?: 'orange' | 'muted' }) {
  return (
    <button
      type="button"
      {...props}
      className={clsx(
        'text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50',
        tone === 'orange' ? 'text-[var(--gt-orange)] hover:underline' : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200',
        className,
      )}
    />
  );
}

/** Red error line (iOS TeamSyncErrorText). Announced to screen readers. */
export function ErrorText({ children }: { children: ReactNode }) {
  if (!children) return null;
  return (
    <p role="alert" className="flex items-start gap-2 text-sm font-medium text-[var(--gt-red)]">
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <span>{children}</span>
    </p>
  );
}

export function Notice({ icon, children }: { icon?: ReactNode; children: ReactNode }) {
  return (
    <p role="status" className="flex items-start gap-2 text-sm text-slate-500 dark:text-slate-400">
      {icon && <span className="mt-0.5 shrink-0">{icon}</span>}
      <span>{children}</span>
    </p>
  );
}

export function Caption({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={clsx('text-xs text-slate-500 dark:text-slate-400', className)}>{children}</p>;
}

/** Thin rule with "or" between the Apple / Google buttons and the email form. */
export function OrDivider() {
  return (
    <div className="flex items-center gap-3 py-1" aria-hidden>
      <div className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
      <span className="text-xs font-semibold text-slate-400">or</span>
      <div className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
    </div>
  );
}

/** Small legal line under the sign-in / sign-up forms (iOS PrivacyConsentLine). */
export function PrivacyConsentLine() {
  return (
    <p className="text-center text-xs text-slate-500 dark:text-slate-400">
      By continuing you agree to our{' '}
      <a
        href="https://groundteam-app.netlify.app/privacy/"
        target="_blank"
        rel="noreferrer"
        className="font-semibold text-[var(--gt-orange)] hover:underline"
      >
        Privacy Policy
      </a>
      .
    </p>
  );
}

/** "Code sent to <address>" with an optional Change button (iOS CodeSentRow). */
export function CodeSentRow({ destination, onChange, changeLabel = 'Change' }: { destination: string; onChange?: () => void; changeLabel?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <div className="min-w-0">
        <p className="text-xs text-slate-500 dark:text-slate-400">Code sent to</p>
        <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{destination}</p>
      </div>
      {onChange && <TextButton onClick={onChange}>{changeLabel}</TextButton>}
    </div>
  );
}
